import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { X, Calendar, FileText, Plus, CreditCard, IndianRupee } from "lucide-react";
import CloudinaryUpload from "./CloudinaryUpload";

export default function BookingModal({ property, room, onClose }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [moveIn, setMoveIn] = useState("");
  const [duration, setDuration] = useState(1);
  const [docs, setDocs] = useState([]);
  const [docUrl, setDocUrl] = useState("");
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [method, setMethod] = useState("razorpay"); // razorpay | stripe
  const [rzpEnabled, setRzpEnabled] = useState(false);

  useEffect(() => {
    api.get("/payments/razorpay/config").then(r => setRzpEnabled(!!r.data.enabled)).catch(()=>{});
  }, []);

  const rent = room ? room.price_per_month : property.price_per_month;
  const deposit = room ? room.deposit || 0 : 0;
  const total = rent * duration + deposit;

  if (!user || typeof user !== "object") {
    return (
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="brut-card bg-[#FDFCF8] p-8 max-w-md w-full text-center" onClick={e=>e.stopPropagation()}>
          <h3 className="font-display font-black text-2xl">Sign in to book</h3>
          <p className="text-[#4A4A4A] mt-2">Create a free Hostro account to book this PG.</p>
          <div className="flex gap-2 mt-5 justify-center">
            <button className="brut-btn" onClick={()=>{onClose(); nav("/login");}}>Sign in</button>
            <button className="brut-btn pink" onClick={()=>{onClose(); nav("/register");}}>Register</button>
          </div>
        </div>
      </div>
    );
  }

  const addDoc = () => { if (docUrl) { setDocs([...docs, docUrl]); setDocUrl(""); } };

  const createBooking = async () => {
    const { data } = await api.post("/bookings", {
      property_id: property.id, room_id: room?.id || null,
      move_in_date: moveIn, duration_months: duration, documents: docs,
    });
    return data;
  };

  const proceedStripe = async () => {
    setBusy(true);
    try {
      const booking = await createBooking();
      const { data } = await api.post("/payments/checkout", { booking_id: booking.id, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Booking failed");
      setBusy(false);
    }
  };

  const proceedRazorpay = async () => {
    if (typeof window.Razorpay === "undefined") {
      toast.error("Razorpay checkout failed to load. Try again.");
      return;
    }
    setBusy(true);
    let booking;
    try {
      booking = await createBooking();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Booking failed");
      setBusy(false); return;
    }
    let order;
    try {
      const r = await api.post("/payments/razorpay/order", { booking_id: booking.id });
      order = r.data;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Payment init failed");
      setBusy(false); return;
    }
    const rzp = new window.Razorpay({
      key: order.key_id,
      amount: order.amount,
      currency: order.currency,
      order_id: order.order_id,
      name: "Hostro",
      description: order.property_title,
      image: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=200&h=200&fit=crop",
      prefill: order.prefill,
      theme: { color: "#2ED573" },
      modal: { ondismiss: () => setBusy(false) },
      handler: async (response) => {
        try {
          await api.post("/payments/razorpay/verify", {
            booking_id: booking.id,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });
          toast.success("Payment confirmed!");
          window.location.href = `/payment/success?booking_id=${booking.id}&provider=razorpay&paid=1`;
        } catch (e) {
          toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Verification failed");
          setBusy(false);
        }
      },
    });
    rzp.on("payment.failed", () => { toast.error("Payment failed. Try again."); setBusy(false); });
    rzp.open();
  };

  const proceed = () => method === "razorpay" ? proceedRazorpay() : proceedStripe();

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="booking-modal">
      <div className="brut-card bg-[#FDFCF8] p-6 md:p-8 max-w-2xl w-full my-8" onClick={e=>e.stopPropagation()}>
        <div className="flex justify-between items-start mb-5">
          <div>
            <span className="brut-tag lime">Book this PG</span>
            <h3 className="font-display font-black text-2xl md:text-3xl mt-2">{property.title}</h3>
            {room && <div className="text-sm mt-1">Room: <strong>{room.name}</strong> · ₹{room.price_per_month}/mo</div>}
          </div>
          <button onClick={onClose} className="brut-border p-2" data-testid="booking-close"><X size={18}/></button>
        </div>

        <div className="flex gap-2 mb-5">
          {[1,2,3].map(s => (
            <div key={s} className={`flex-1 brut-border p-2 text-center text-xs font-bold ${step===s ? "bg-[#2ED573]" : "bg-white"}`}>
              STEP {s} — {s===1?"DETAILS":s===2?"DOCS":"PAY"}
            </div>
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-4" data-testid="booking-step-1">
            <div>
              <label className="label-eyebrow flex items-center gap-1"><Calendar size={12}/> Move-in date</label>
              <input type="date" className="brut-input mt-1" value={moveIn} onChange={e=>setMoveIn(e.target.value)} required data-testid="booking-date"/>
            </div>
            <div>
              <label className="label-eyebrow">Duration (months)</label>
              <input type="number" min="1" max="24" className="brut-input mt-1" value={duration} onChange={e=>setDuration(parseInt(e.target.value)||1)} data-testid="booking-duration"/>
            </div>
            <div className="brut-card p-4 bg-[#ECFAF1]">
              <div className="flex justify-between"><span>Rent / month</span><span>₹{rent.toLocaleString()}</span></div>
              <div className="flex justify-between"><span>× {duration} month{duration>1?"s":""}</span><span>₹{(rent*duration).toLocaleString()}</span></div>
              {deposit > 0 && <div className="flex justify-between"><span>Security deposit</span><span>₹{deposit.toLocaleString()}</span></div>}
              <div className="flex justify-between font-display font-black text-xl mt-2 pt-2 border-t-2 border-[#0F1F17]"><span>Total</span><span>₹{total.toLocaleString()}</span></div>
            </div>
            <button className="brut-btn w-full" onClick={()=>setStep(2)} disabled={!moveIn} data-testid="booking-next-1">Next: Upload documents</button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4" data-testid="booking-step-2">
            <p className="text-sm text-[#4A4A4A]">Upload ID proof (Aadhaar/Passport), college ID (for students). Files are stored securely on Cloudinary.</p>
            <CloudinaryUpload
              folder="hostro/documents"
              multiple
              value={docs}
              onChange={setDocs}
              accept="image/*,application/pdf"
              resourceType="raw"
              testId="booking-docs-upload"
            />
            <details>
              <summary className="text-xs text-[#4A4A4A] cursor-pointer">Or paste a URL manually</summary>
              <div className="flex gap-2 mt-2">
                <input className="brut-input" value={docUrl} onChange={e=>setDocUrl(e.target.value)} placeholder="https://..." data-testid="doc-input"/>
                <button type="button" className="brut-btn white" onClick={addDoc} data-testid="doc-add"><Plus size={16}/></button>
              </div>
            </details>
            <div className="flex gap-2">
              <button className="brut-btn white flex-1" onClick={()=>setStep(1)}>Back</button>
              <button className="brut-btn flex-1" onClick={()=>setStep(3)} data-testid="booking-next-2">Next: Payment</button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4" data-testid="booking-step-3">
            <div className="brut-card p-5 bg-[#2ED573]">
              <div className="label-eyebrow">Pay securely</div>
              <div className="font-display font-black text-4xl mt-1">₹{total.toLocaleString()}</div>
              <p className="text-sm mt-2">Choose your preferred payment method.</p>
            </div>

            <div>
              <label className="label-eyebrow mb-2 block">Payment method</label>
              <div className="grid grid-cols-2 gap-3">
                <button type="button" onClick={()=>setMethod("razorpay")} disabled={!rzpEnabled}
                  className={`brut-card p-4 text-left ${method==="razorpay"?"bg-[#2ED573]":""} ${!rzpEnabled?"opacity-40":""}`} data-testid="pm-razorpay">
                  <div className="flex items-center gap-2">
                    <IndianRupee size={18}/><strong>Razorpay</strong>
                    {!rzpEnabled && <span className="brut-tag white !text-[9px]">offline</span>}
                  </div>
                  <div className="text-xs mt-1">UPI, Cards, Netbanking, Wallets · INR</div>
                </button>
                <button type="button" onClick={()=>setMethod("stripe")}
                  className={`brut-card p-4 text-left ${method==="stripe"?"bg-[#FF90E8]":""}`} data-testid="pm-stripe">
                  <div className="flex items-center gap-2"><CreditCard size={18}/><strong>Stripe</strong></div>
                  <div className="text-xs mt-1">International cards · USD (demo)</div>
                </button>
              </div>
            </div>

            <div className="text-xs text-[#4A4A4A] brut-border p-3 bg-white">
              {method === "razorpay" ?
                <><strong>Razorpay test mode:</strong> use card <code>4111 1111 1111 1111</code>, any future date, any CVC. Or UPI ID <code>success@razorpay</code>.</>
                :
                <><strong>Stripe test mode:</strong> use card <code>4242 4242 4242 4242</code>, any future date, any CVC.</>
              }
            </div>
            <div className="flex gap-2">
              <button className="brut-btn white flex-1" onClick={()=>setStep(2)} disabled={busy}>Back</button>
              <button className="brut-btn flex-1" onClick={proceed} disabled={busy} data-testid="proceed-payment-btn">
                {busy ? "Processing..." : `Pay with ${method === "razorpay" ? "Razorpay" : "Stripe"}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
